/**
 * 계정 관리 API 서비스
 *
 * AICC 시스템의 테넌트(보험사/금융사), 채널(상담 채널), 사용자(상담원/관리자) 관리 API.
 * 멀티테넌트 환경에서 조직 구조와 접근 권한을 설정하는 핵심 서비스.
 *
 * 【사용법】
 * ```javascript
 * import { defaultAccountAPI } from './accountApi';
 *
 * const tenants = await defaultAccountAPI.getAllTenants();
 * const newChannel = await defaultAccountAPI.createChannel(tenantId, channelData);
 * ```
 */

import { API_BASE_URL } from '../../constants/api';
import { apiRequest } from './apiClient';

/**
 * Account API class
 */
export class AccountAPI {
  /**
   * @param {Object} config
   * @param {string} config.baseUrl - API base URL (default: API_BASE_URL)
   * @param {Object} config.endpoints - Endpoint paths
   * @param {Object} config.logger - Logger object (default: console)
   */
  constructor({
    baseUrl = API_BASE_URL,
    endpoints = {
      tenants: '/api/account/tenant',
      channels: '/api/account/channel',
      users: '/api/account/user',
    },
    logger = console,
  } = {}) {
    this.baseUrl = baseUrl;
    this.endpoints = endpoints;
    this.logger = logger;
  }

  // ==================== Tenant APIs ====================

  /**
   * Get all tenants (returns array)
   * @returns {Promise<TenantResponse[]>}
   */
  async getAllTenants() {
    const searchBody = { pageSize: 1000 };
    const response = await apiRequest(
      `${this.baseUrl}${this.endpoints.tenants}/search`,
      { method: 'POST', body: JSON.stringify(searchBody) },
      this.logger
    );
    return response?.content || response || [];
  }

  /**
   * Get tenants (paged)
   * @param {Object} params - { page, size, query, enabled, sortCode }
   * @returns {Promise<Page<TenantResponse>>}
   */
  async getTenants(params = {}) {
    const searchBody = {};
    if (params.page !== undefined) searchBody.pageNumber = params.page;
    if (params.size !== undefined) searchBody.pageSize = params.size;
    if (params.query) searchBody.tenantName = params.query;
    if (params.enabled !== undefined) searchBody.enabled = params.enabled;
    if (params.sortCode) searchBody.sortCode = params.sortCode;

    return apiRequest(
      `${this.baseUrl}${this.endpoints.tenants}/search`,
      { method: 'POST', body: JSON.stringify(searchBody) },
      this.logger
    );
  }

  /**
   * Get tenant by ID
   * @param {string} tenantId
   * @returns {Promise<TenantResponse>}
   */
  async getTenant(tenantId) {
    return apiRequest(`${this.baseUrl}${this.endpoints.tenants}/${tenantId}`, {}, this.logger);
  }

  /**
   * Create tenant
   * @param {Object} data - { tenantCode, name, contactEmail, metadata }
   * @returns {Promise<TenantResponse>}
   */
  async createTenant(data) {
    return apiRequest(
      `${this.baseUrl}${this.endpoints.tenants}`,
      { method: 'POST', body: JSON.stringify(data) },
      this.logger
    );
  }

  /**
   * Update tenant
   * @param {string} tenantId
   * @param {Object} data - { name, contactEmail, metadata }
   * @returns {Promise<TenantResponse>}
   */
  async updateTenant(tenantId, data) {
    // ElastiCore: PUT / with ID in body (no path variable)
    return apiRequest(
      `${this.baseUrl}${this.endpoints.tenants}`,
      { method: 'PUT', body: JSON.stringify({ ...data, tenantId }) },
      this.logger
    );
  }

  /**
   * Delete tenant
   * @param {string} tenantId
   * @returns {Promise<void>}
   */
  async deleteTenant(tenantId) {
    return apiRequest(
      `${this.baseUrl}${this.endpoints.tenants}/${tenantId}`,
      { method: 'DELETE' },
      this.logger
    );
  }

  /**
   * Enable tenant
   * @param {string} tenantId
   * @returns {Promise<TenantResponse>}
   */
  async enableTenant(tenantId) {
    return apiRequest(
      `${this.baseUrl}${this.endpoints.tenants}/${tenantId}/enable`,
      { method: 'POST' },
      this.logger
    );
  }

  /**
   * Disable tenant
   * @param {string} tenantId
   * @returns {Promise<TenantResponse>}
   */
  async disableTenant(tenantId) {
    return apiRequest(
      `${this.baseUrl}${this.endpoints.tenants}/${tenantId}/disable`,
      { method: 'POST' },
      this.logger
    );
  }

  /**
   * Regenerate API key
   * @param {string} tenantId
   * @returns {Promise<TenantResponse>}
   */
  async regenerateApiKey(tenantId) {
    return apiRequest(
      `${this.baseUrl}${this.endpoints.tenants}/${tenantId}/regenerate-api-key`,
      { method: 'POST' },
      this.logger
    );
  }

  // ==================== Channel APIs ====================

  /**
   * Get channels for a tenant
   * @param {string} tenantId
   * @returns {Promise<ChannelResponse[]>}
   */
  async getChannels(tenantId) {
    const searchBody = { tenantId: tenantId, pageSize: 1000 };
    const response = await apiRequest(
      `${this.baseUrl}${this.endpoints.channels}/search`,
      { method: 'POST', body: JSON.stringify(searchBody) },
      this.logger
    );
    return response?.content || response || [];
  }

  /**
   * Get all channels (Admin, paged)
   * @param {Object} params - { page, size, tenantId, channelType, state }
   * @returns {Promise<Page<ChannelResponse>>}
   */
  async getAllChannels(params = {}) {
    const searchBody = {};
    if (params.page !== undefined) searchBody.pageNumber = params.page;
    if (params.size !== undefined) searchBody.pageSize = params.size;
    if (params.tenantId) searchBody.tenantId = params.tenantId;
    if (params.channelType) searchBody.channelType = params.channelType;
    if (params.state) searchBody.state = params.state;

    return apiRequest(
      `${this.baseUrl}${this.endpoints.channels}/search`,
      { method: 'POST', body: JSON.stringify(searchBody) },
      this.logger
    );
  }

  /**
   * Get channel by ID
   * @param {string} tenantId - Not used (kept for API compatibility)
   * @param {string} channelId
   * @returns {Promise<ChannelResponse>}
   */
  async getChannel(tenantId, channelId) {
    return apiRequest(`${this.baseUrl}${this.endpoints.channels}/${channelId}`, {}, this.logger);
  }

  /**
   * Create channel
   * @param {string} tenantId
   * @param {Object} data - { channelName, channelType, description, metadata }
   * @returns {Promise<ChannelResponse>}
   */
  async createChannel(tenantId, data) {
    return apiRequest(
      `${this.baseUrl}${this.endpoints.channels}`,
      { method: 'POST', body: JSON.stringify({ ...data, tenantId: tenantId }) },
      this.logger
    );
  }

  /**
   * Update channel
   * @param {string} tenantId
   * @param {number} channelId - Channel numeric id (Long)
   * @param {Object} data - { channelName, description, metadata }
   * @returns {Promise<ChannelResponse>}
   */
  async updateChannel(tenantId, channelId, data) {
    // ElastiCore: PUT / with ID in body (no path variable)
    return apiRequest(
      `${this.baseUrl}${this.endpoints.channels}`,
      { method: 'PUT', body: JSON.stringify({ ...data, id: channelId }) },
      this.logger
    );
  }

  /**
   * Delete channel
   * @param {string} tenantId - Not used (kept for API compatibility)
   * @param {string} channelId
   * @returns {Promise<void>}
   */
  async deleteChannel(tenantId, channelId) {
    return apiRequest(
      `${this.baseUrl}${this.endpoints.channels}/${channelId}`,
      { method: 'DELETE' },
      this.logger
    );
  }

  /**
   * Change channel state
   * @param {string} tenantId - Not used (kept for API compatibility)
   * @param {string} channelId
   * @param {Object} data - { targetState, reason }
   * @returns {Promise<ChannelResponse>}
   */
  async changeChannelState(tenantId, channelId, data) {
    const searchParams = new URLSearchParams();
    if (tenantId) searchParams.append('tenantId', tenantId);
    const queryString = searchParams.toString();
    const url = `${this.baseUrl}${this.endpoints.channels}/${channelId}/state${queryString ? `?${queryString}` : ''}`;
    return apiRequest(url, { method: 'POST', body: JSON.stringify(data) }, this.logger);
  }

  /**
   * Get channel state transition history
   * @param {string} tenantId - Not used (kept for API compatibility)
   * @param {string} channelId
   * @param {Object} params - { page, size }
   * @returns {Promise<Page<ChannelStateTransitionResponse>>}
   */
  async getChannelTransitions(tenantId, channelId, params = {}) {
    const searchParams = new URLSearchParams();
    if (tenantId) searchParams.append('tenantId', tenantId);
    if (params.page !== undefined) searchParams.append('page', params.page);
    if (params.size !== undefined) searchParams.append('size', params.size);

    const queryString = searchParams.toString();
    const url = `${this.baseUrl}${this.endpoints.channels}/${channelId}/transitions${queryString ? `?${queryString}` : ''}`;
    return apiRequest(url, {}, this.logger);
  }

  // ==================== User APIs ====================

  /**
   * Transform backend user to frontend format
   */
  _transformUser(user) {
    if (!user) return user;
    return {
      ...user,
      name: user.username || user.name,
    };
  }

  /**
   * Get users (paged)
   * @param {Object} params - { page, size }
   * @returns {Promise<Page<UserResponse>>}
   */
  async getUsers(params = {}) {
    const searchBody = {};
    if (params.page !== undefined) searchBody.pageNumber = params.page;
    if (params.size !== undefined) searchBody.pageSize = params.size;

    const response = await apiRequest(
      `${this.baseUrl}/api/account/user/search`,
      { method: 'POST', body: JSON.stringify(searchBody) },
      this.logger
    );

    // Transform username -> name for frontend compatibility
    if (response?.content) {
      response.content = response.content.map((user) => this._transformUser(user));
    }
    return response;
  }

  /**
   * Get user by ID
   * @param {string} userId
   * @returns {Promise<UserResponse>}
   */
  async getUser(userId) {
    const user = await apiRequest(`${this.baseUrl}/api/account/user/${userId}`, {}, this.logger);
    return this._transformUser(user);
  }

  /**
   * Create user (uses custom endpoint with proper password hashing)
   * @param {Object} data - { email, password, name, role, tenantId }
   * @returns {Promise<UserResponse>}
   */
  async createUser(data) {
    const requestBody = {
      email: data.email,
      password: data.password,
      name: data.name || data.username,
      role: data.role || 'USER',
      tenantId: data.tenantId || null,
    };

    return apiRequest(
      `${this.baseUrl}/api/account/user/create-custom`,
      { method: 'POST', body: JSON.stringify(requestBody) },
      this.logger
    );
  }

  /**
   * Update user (uses custom endpoint with proper password hashing)
   * @param {string} userId
   * @param {Object} data - { email, password, name, role, tenantId }
   * @returns {Promise<UserResponse>}
   */
  async updateUser(userId, data) {
    const requestBody = {};
    if (data.email) requestBody.email = data.email;
    if (data.name || data.username) requestBody.name = data.name || data.username;
    if (data.password) requestBody.password = data.password;
    if (data.role) requestBody.role = data.role;
    if (data.tenantId !== undefined) requestBody.tenantId = data.tenantId;

    return apiRequest(
      `${this.baseUrl}/api/account/user/${userId}/update-custom`,
      { method: 'PUT', body: JSON.stringify(requestBody) },
      this.logger
    );
  }

  /**
   * Delete user
   * @param {string} userId
   * @returns {Promise<void>}
   */
  async deleteUser(userId) {
    return apiRequest(
      `${this.baseUrl}/api/account/user/${userId}`,
      { method: 'DELETE' },
      this.logger
    );
  }

  /**
   * Enable user
   * @param {string} userId
   * @returns {Promise<UserResponse>}
   */
  async enableUser(userId) {
    return apiRequest(
      `${this.baseUrl}/api/account/user/${userId}/enable`,
      { method: 'POST' },
      this.logger
    );
  }

  /**
   * Disable user
   * @param {string} userId
   * @returns {Promise<UserResponse>}
   */
  async disableUser(userId) {
    return apiRequest(
      `${this.baseUrl}/api/account/user/${userId}/disable`,
      { method: 'POST' },
      this.logger
    );
  }

  /**
   * Search users
   * @param {Object} params - { keyword, role, enabled, page, size }
   * @returns {Promise<Page<UserResponse>>}
   */
  async searchUsers(params = {}) {
    const searchBody = {};
    if (params.keyword) searchBody.username = `%${params.keyword}%`;
    if (params.role) searchBody.role = params.role;
    if (params.enabled !== undefined) searchBody.enabled = params.enabled;
    if (params.page !== undefined) searchBody.pageNumber = params.page;
    if (params.size !== undefined) searchBody.pageSize = params.size;

    return apiRequest(
      `${this.baseUrl}/api/account/user/search`,
      { method: 'POST', body: JSON.stringify(searchBody) },
      this.logger
    );
  }

  /**
   * Get users by tenant
   * @param {string} tenantId
   * @param {Object} params - { page, size }
   * @returns {Promise<Page<UserResponse>>}
   */
  async getUsersByTenant(tenantId, params = {}) {
    const searchBody = { tenantId };
    if (params.page !== undefined) searchBody.pageNumber = params.page;
    if (params.size !== undefined) searchBody.pageSize = params.size;

    return apiRequest(
      `${this.baseUrl}/api/account/user/search`,
      { method: 'POST', body: JSON.stringify(searchBody) },
      this.logger
    );
  }

  /**
   * Get users by role
   * @param {string} role - GLOBAL_ADMIN, TENANT_ADMIN, USER
   * @param {Object} params - { page, size }
   * @returns {Promise<Page<UserResponse>>}
   */
  async getUsersByRole(role, params = {}) {
    const searchBody = { role };
    if (params.page !== undefined) searchBody.pageNumber = params.page;
    if (params.size !== undefined) searchBody.pageSize = params.size;

    return apiRequest(
      `${this.baseUrl}/api/account/user/search`,
      { method: 'POST', body: JSON.stringify(searchBody) },
      this.logger
    );
  }
}

export const defaultAccountAPI = new AccountAPI();
